import { AfterViewInit, ChangeDetectionStrategy, Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';

import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

import { GridActions, GridService } from '@core/services';
import { remToPx } from '@util/layout';

import { FacilityLookupResponseRow } from '#types/facility';
import { Filters } from '#types/grid';

import { AuthService } from '@auth/services';
import { CONSTANTS } from '@facility/constants';
import { FacilityService } from '@facility/services';
import { ShipmentService } from '@shipment/services';

@Component({
  selector: 'z-facility-lookup-dialog',
  templateUrl: './facility-lookup-dialog.component.html',
  styleUrls: ['./facility-lookup-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FacilityLookupDialogComponent implements OnInit, OnDestroy, AfterViewInit {
  destroy$ = new Subject<void>();

  stateOrProvinceList$ = this.shipmentService.stateOrProvinceDropdownList$;

  form: FormGroup;

  facility: string;
  name: string;

  selectedRow: FacilityLookupResponseRow;

  matchTypes: Record<string, string> = {};

  container: HTMLDivElement;
  containerParent: HTMLDivElement;
  containerFilters: HTMLDivElement;
  containerResizeObserver: ResizeObserver;

  lookupId = 'facility-lookup-dialog';

  gridId = CONSTANTS.GRID_FACILITY_LOOKUP_DIALOG;

  constructor(
    private store: Store,
    private shipmentService: ShipmentService,
    private facilityService: FacilityService,
    private ref: DynamicDialogRef,
    private config: DynamicDialogConfig,
    private gridService: GridService,
    private authService: AuthService,
    private ngZone: NgZone
  ) {}

  ngOnInit() {
    this.form = new FormGroup({
      facility: new FormControl(),
      name: new FormControl(),
      stateOrProvince: new FormControl(),
      campus: new FormControl(),
    });

    this.shipmentService.requestLists();
    this.facilityService.requestLists();

    this.gridService
      .selectedRow$<FacilityLookupResponseRow>(this.gridId)
      .pipe(
        filter((x) => !!x),
        takeUntil(this.destroy$)
      )
      .subscribe((selectedRow) => {
        this.selectedRow = selectedRow.data;
        this.facility = this.selectedRow.facility;
        this.name = this.selectedRow.name;

        if (selectedRow.selectionType === 'double') {
          this.onSelectClick();
        }
      });

    // close dialog if no longer authenticated
    this.authService.authenticated$
      .pipe(
        filter((authenticated) => !authenticated),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.onCancelClick();
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.unsubscribe();

    this.destroyResizeObserver();
  }

  ngAfterViewInit() {
    this.initResizeObserver();
  }

  initResizeObserver() {
    this.containerResizeObserver = new ResizeObserver(() => {
      this.ngZone.run(() => {
        const header: HTMLDivElement = this.containerParent.querySelector('.p-dialog-header');
        const filters: HTMLDivElement = this.container.querySelector('.filters');
        const footer: HTMLDivElement = this.container.querySelector('.p-dialog-footer');
        const paginator: HTMLDivElement = this.container.querySelector('.p-paginator');

        let offset = 0;

        if (header) {
          offset += Math.floor(header.offsetHeight);
        }

        if (filters) {
          offset += Math.floor(filters.offsetHeight);
        }

        if (footer) {
          offset += Math.floor(footer.offsetHeight);
        }

        if (paginator) {
          offset += Math.floor(paginator.offsetHeight);
        }

        this.facilityService.facilityLookupDialogDimensions$.next({
          container: {
            width: Math.floor(this.containerParent.offsetWidth),
            height: Math.floor(this.containerParent.offsetHeight),
          },
          grid: {
            width: Math.floor(this.containerParent.offsetWidth) - remToPx('2rem'),
            height: Math.floor(this.containerParent.offsetHeight) - offset,
          },
        });
      });
    });

    try {
      this.container = document.querySelector(`.${this.lookupId}`);

      if (this.container) {
        this.containerParent = this.container.closest(`.lookup-dialog`);

        if (this.containerParent) {
          this.containerResizeObserver.observe(this.containerParent);
        }

        this.containerFilters = this.container.querySelector('.filters');

        if (this.containerFilters) {
          this.containerResizeObserver.observe(this.containerFilters);
        }
      }
    } catch {}
  }

  destroyResizeObserver() {
    try {
      if (this.containerResizeObserver && this.containerParent) {
        this.containerResizeObserver.unobserve(this.containerParent);
      }

      if (this.containerResizeObserver && this.containerFilters) {
        this.containerResizeObserver.unobserve(this.containerFilters);
      }
    } catch {}
  }

  onSelectClick() {
    this.ref.close({ facility: this.facility, name: this.name, selectedRow: this.selectedRow });
  }

  onCancelClick() {
    this.ref.close(undefined);
  }

  onMatchTypeChange(field: string, matchType: string) {
    this.matchTypes[field] = matchType;
  }

  applyFilters() {
    const filters: Filters = {};

    if (this.form.get('facility').value) {
      filters.facility = { value: this.form.get('facility').value, matchMode: this.matchTypes.facility ?? 'equals' };
    }

    if (this.form.get('name').value) {
      filters.name = { value: this.form.get('name').value, matchMode: this.matchTypes.name ?? 'startsWith' };
    }

    if (this.form.get('stateOrProvince').value) {
      filters.stateOrProvince = { value: this.form.get('stateOrProvince').value };
    }

    if (this.form.get('campus').value) {
      filters.campus = { value: this.form.get('campus').value };
    }

    this.store.dispatch(GridActions.SetFilters({ id: this.gridId, filters }));
  }

  reset(): void {
    this.form.setValue({
      facility: null,
      name: null,
      stateOrProvince: null,
      campus: null,
    });
  }

  isInvalid(formControlName: string) {
    return !this.form.controls[formControlName].pristine && this.form.controls[formControlName].invalid;
  }
}
